import { HUDLabel, HUD_LABEL_TRANSFORM_EVENT } from './HudLabel';
import { HUDPinLabel } from './HudPinLabel';
import { HudLayers } from './layers/HudLayers';
import { LmvMatrix4 } from '../../lmv-compat/math/LmvMatrix4';
import { LmvBox3 } from '../../lmv-compat/math/LmvBox3';

const boxArr = new Array(6);
const _tmpBox = new LmvBox3();
const _tmpMatrix = new LmvMatrix4();

const HUD_LABEL_PADDING = 5;
const DOM_LABELS_OPACITY_MIN = 0.55;
const HUD_TOOL_NAME = 'dt_hud';

function makeLabelId(model, dbId, isPin) {
	return `${model.urn()}:${dbId}${isPin ? ':pin' : ''}`;
}

export class HUD {
	#drawQueue = [];
	#hasAnimations = false;
	#animationFrameRequested = false;
	#nextFrameCallbacks = [];

	/**
	 * @param {Viewer3D} viewer
	 * @param {DtFacility} twin
	 */
	constructor(viewer, twin) {
		this.viewer = viewer;
		this.twin = twin;

		this.labels = {};
		this.layers = new HudLayers(viewer, twin, this);
	}

	#init() {
		if (!this.layers) {
			this.layers = new HudLayers(this.viewer, this.twin, this);
		}
		this.layers.init(this.viewer);

		const document = this.viewer.container.ownerDocument;

		// Background canvas for static elements
		this.canvas = document.createElement("canvas");
		this._initCanvas(this.canvas);
		this.ctx = this.canvas.getContext("2d");

		// Foreground canvas for animated elements
		this.animCanvas = document.createElement("canvas");
		this._initCanvas(this.animCanvas);
		this.animCtx = this.animCanvas.getContext("2d");

		// Append layers last to ensure correct z-index stacking
		this.viewer.canvasWrap.appendChild(this.layers.dom);

		this.dpr = window.devicePixelRatio;
		this.width = this.viewer.canvasWrap.clientWidth * this.dpr;
		this.height = this.viewer.canvasWrap.clientHeight * this.dpr;
	}

	_initCanvas(canvas) {
		canvas.style.position = "absolute";
		canvas.style.width = "100%";
		canvas.style.height = "100%";
		canvas.style.left = "0";
		canvas.style.top = "0";
		canvas.style.pointerEvents = "none";
		canvas.style.backgroundColor = "rgba(0,0,0,0)";
		this.viewer.canvasWrap.appendChild(canvas);
	}

	/** @see ToolController */
	activate(_toolName, viewer) {
		this.viewer = viewer;
		this.#init();
	}

	/** @see ToolController */
	deactivate(/* toolName */) {
		this.#dtor();
	}

	#dtor() {
		if (this.layers) {
			this.layers.dispose();
			this.viewer.canvasWrap.removeChild(this.layers.dom);
			this.layers = null;
		}

		if (this.labelsListener) {
			this.viewer.removeEventListener(HUD_LABEL_TRANSFORM_EVENT, this.labelsListener);
			this.labelsListener = null;
		}

		if (this.canvas) {
			this.viewer.canvasWrap.removeChild(this.canvas);
			this.canvas = null;
		}

		if (this.animCanvas) {
			this.viewer.canvasWrap.removeChild(this.animCanvas);
			this.animCanvas = null;
		}
	}

	resize(width, height) {
		this.dpr = window.devicePixelRatio;
		this.width = width * this.dpr;
		this.height = height * this.dpr;

		this.canvas.width = this.width;
		this.canvas.height = this.height;

		this.animCanvas.width = this.width;
		this.animCanvas.height = this.height;
	}

	getLabels() {
		return this.labels;
	}

	addLabel(model, dbId, isPin, options) {
		const labelId = makeLabelId(model, dbId, isPin);

		let label = this.getLabel(labelId);
		if (label) {
			// don't replace existing label
			return label;
		}

		const it = model.getInstanceTree();

		it.getNodeBox(dbId, boxArr);
		_tmpBox.min.set(boxArr[0], boxArr[1], boxArr[2]);
		_tmpBox.max.set(boxArr[3], boxArr[4], boxArr[5]);

		const elementName = it.getNodeName(dbId, false);
		const LabelClass = isPin ? HUDPinLabel : HUDLabel;
		label = new LabelClass(this.viewer, _tmpBox.getCenter(), elementName, labelId, options);
		this.labels[labelId] = label;

		label.setWorldBox(_tmpBox.clone(), 5, 50);

		return label;
	}

	getDocument() {
		return this.viewer.canvasWrap.ownerDocument;
	}

	getLabel(labelId) {
		return this.labels[labelId];
	}

	getElementLabel(element) {
		const model = element.model;
		const dbId = element.dbId;
		const labelId = makeLabelId(model, dbId);
		return this.labels[labelId];
	}

	/** @see ToolController */
	getName() {
		return HUD_TOOL_NAME;
	}

	/** @see ToolController */
	getNames() {
		return [HUD_TOOL_NAME];
	}

	deleteLabel(labelId) {
		this.labels[labelId]?.dispose();
		delete this.labels[labelId];
	}

	removeDrawStep(drawStep) {
		this.#drawQueue = this.#drawQueue.filter(step => step !== drawStep);
		this.#hasAnimations = this.#drawQueue.some(step => step[1].drawAnim);

		// Clear canvas in case we emptied the draw queue
		this.ctx.clearRect(0, 0, this.width, this.height);
		this.animCtx.clearRect(0, 0, this.width, this.height);

		this.repaint();
	}

	addDrawStep(drawStep) {
		// One step is split into update + draw functions
		this.#drawQueue.push(drawStep);
		this.#drawQueue.sort((a, b) => a[0] - b[0]);
		this.#hasAnimations ||= drawStep[1].drawAnim;

		this.repaint();
	}

	update(ts) {
		const dirty = this.layers.update(ts);

		if (dirty) {
			this.layers.compose();

			// we likely want to integrate label z-depth post-process in compositor.
			this.updateDOMLabelsByDepth();
		}

		if (this.#hasAnimations) {
			this.#updateAnimations(ts);
		}

		return false;
	}

	updateDOMLabelsByDepth() {
		const camera = this.viewer.impl.camera;
		const depthByLabel = [];

		for (const labelId in this.labels) {
			const label = this.labels[labelId];
			const depth = camera.position.distanceToSquared(label.pos3D);

			depthByLabel.push([label, depth]);
		}

		const count = depthByLabel.length;
		if (count <= 1) return; // No sorting needed

		depthByLabel.sort((a, b) => b[1] - a[1]);

		const minDepth = depthByLabel[count - 1][1];
		const maxDepth = depthByLabel[0][1];
		const range = maxDepth - minDepth;

		if (range === 0) return; // No distance delta

		for (let i = 0; i < count; i++) {
			const label = depthByLabel[i][0];

			// z-index is equal to distance-to-camera based ordering,
			// plus the base value of the container (measure-length CSS style)
			label.baseZIndex = i + 2;
		}
	}

	repaint() {
		if (this.#drawQueue.length === 0) return;

		const camera = this.viewer.getCamera()
		_tmpMatrix.copy(camera.matrixWorldInverse);
		_tmpMatrix.multiplyMatrices(camera.projectionMatrix, _tmpMatrix);

		this.ctx.clearRect(0, 0, this.width, this.height);
		this.animCtx.clearRect(0, 0, this.width, this.height);


		for (const [_, { init, drawStatic, drawAnim }] of this.#drawQueue) {
			// initialize animation and redraw immediately since the animation canvas was cleared
			init(this, _tmpMatrix);
			drawStatic?.(this);
			drawAnim?.(this);
		}
	}

	#updateAnimations(ts) {
		let animDirty = false;

		// Call animation updates
		for (const [_, { update }] of this.#drawQueue) {
			animDirty = update?.(ts) || animDirty;
		}

		// Repaint if updated
		if (animDirty) {
			this.animCtx.clearRect(0, 0, this.width, this.height);
			for (const [_, { drawAnim }] of this.#drawQueue) {
				drawAnim?.(this);
			}
		}
	}

	getStateForView() {
		const hudState = {};

		if (this.layers) {
			hudState.layers = this.layers.getStateForView();
		}

		return hudState;
	}

	handleMouseMove(event) {
		for (const layer of this.layers.layers) {
			if (layer.handleMouseMove(event)) {
				return true;
			}
		}
	}

	registerNextFrameCallback(callback) {
		this.#nextFrameCallbacks.push(callback);
		if (!this.#animationFrameRequested) {
			this.#animationFrameRequested = true;
			requestAnimationFrame(() => {
				this.#animationFrameRequested = false;
				for (const callback of this.#nextFrameCallbacks) {
					callback();
				}
				this.#nextFrameCallbacks.length = 0;
			});
		}
	}
}
